/**
 * The waiting room's shared pieces: a link to send friends, and everyone who has joined so far.
 * Both the creator's lobby and a joined phone show them, from the same live snapshot.
 */

import { useEffect, useRef, useState } from 'react';
import type { Snapshot } from '@gsf/contract';

/**
 * What a phone needs from the public session lookup: the join link, served by the server (it knows
 * the LAN IP or public origin, never "localhost"), and whether this is a Play Now game.
 */
export function useSessionInfo(code: string): { joinUrl: string | null; playNow: boolean } {
  const [info, setInfo] = useState<{ joinUrl: string | null; playNow: boolean }>({ joinUrl: null, playNow: false });
  useEffect(() => {
    let live = true;
    fetch(`/api/sessions/${encodeURIComponent(code)}`)
      .then((res) => (res.ok ? (res.json() as Promise<{ joinUrl?: string; playNow?: boolean }>) : null))
      .then((body) => {
        if (live && body) setInfo({ joinUrl: body.joinUrl ?? null, playNow: body.playNow === true });
      })
      .catch(() => {
        /* the code on screen still works without the link */
      });
    return () => {
      live = false;
    };
  }, [code]);
  return info;
}

/**
 * The link in a box with a Copy button. The box stays so the link can be copied by hand: the
 * clipboard API needs a secure origin, and a LAN address over plain http is not one.
 */
export function CopyLink({ url }: { url: string | null }) {
  const box = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  /** The copy failed: say how to do it by hand. */
  const [manual, setManual] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setManual(false);
    } catch {
      // No clipboard API here (plain-http LAN): select the text so a long-press copies it, and try
      // the legacy command, which still works in most browsers.
      box.current?.select();
      const ok = document.execCommand('copy');
      setCopied(ok);
      setManual(!ok);
    }
  }

  if (!url) return null;
  return (
    <div className="field">
      <label htmlFor="lobby-link">Invite friends</label>
      <div className="row" style={{ gap: '0.4rem' }}>
        <input
          id="lobby-link"
          ref={box}
          readOnly
          value={url}
          onFocus={(e) => e.target.select()}
          style={{ flex: 1, minWidth: 0 }}
        />
        <button className="btn btn-primary" type="button" onClick={copy}>
          {copied ? 'Copied' : 'Copy Link'}
        </button>
        {/* `sms:` over `navigator.share()`: the share sheet needs a secure origin, and LAN play is
            plain http. `?&body=` is the form both iOS and Android read. */}
        <a className="btn touch-only" href={`sms:?&body=${encodeURIComponent(`Join my game: ${url}`)}`}>
          Text Link
        </a>
      </div>
      {manual ? <span className="faint">The link is selected: press and hold it to copy.</span> : null}
      <span className="sr-only" aria-live="polite">
        {copied ? 'Link copied' : ''}
      </span>
    </div>
  );
}

/** Everyone seated, in join order, updating live as phones join. */
export function JoinedPlayers({ snapshot }: { snapshot: Snapshot }) {
  const players = snapshot.core.players;
  const me = snapshot.you.participantId;
  return (
    <div className="col" style={{ gap: '0.4rem' }}>
      <h2 className="lobby-title" aria-live="polite">
        Joined Players ({players.length})
      </h2>
      {players.length === 0 ? (
        <p className="faint" style={{ margin: 0 }}>
          Nobody yet.
        </p>
      ) : (
        <ul className="lobby-players">
          {players.map((p) => (
            <li key={p.id} className={p.connected ? undefined : 'is-away'}>
              {p.name}
              {p.id === me ? <span className="faint"> (you)</span> : null}
              {p.connected ? null : <span className="faint"> (away)</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
